import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Card,
  CardHeader,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Newsfeed } from '../../types/newsfeed';
import { TableHeadCell } from '../../types/table';
import TablePeo from '../../Components/TablePeo';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getNewsfeeds } from '../../endpoints/newsfeedTs';

interface NewsfeedFilters {
  search: string;
  isActive: string;
}

export default function NewsfeedList(): React.JSX.Element {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<NewsfeedFilters>({
    search: '',
    isActive: 'all',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('title');

  // Fetch newsfeeds from API
  const {
    data: newsfeeds = [],
    isLoading,
    error,
  } = useApiGet(['newsfeeds'], getNewsfeeds, {
    enabled: true,
    retry: 1,
  });

  // Table header cells
  const headCells: TableHeadCell[] = [
    {
      id: 'title',
      label: 'Title',
      width: '40%',
      firstCell: true,
      sortable: true,
    },
    { id: 'start_date', label: 'Start Date', width: '20%', sortable: true },
    { id: 'end_date', label: 'End Date', width: '20%', sortable: true },
    { id: 'is_active', label: 'Status', width: '20%', sortable: true },
  ];

  // Filter and sort newsfeeds
  const filteredAndSortedNewsfeeds = useMemo(() => {
    let filtered = newsfeeds.filter((newsfeed) => {
      const matchesSearch = newsfeed.title
        .toLowerCase()
        .includes(filters.search.toLowerCase());

      const matchesActiveFilter =
        filters.isActive === 'all' ||
        (filters.isActive === 'active' && newsfeed.is_active) ||
        (filters.isActive === 'inactive' && !newsfeed.is_active);

      return matchesSearch && matchesActiveFilter;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      const aValue = a[orderBy as keyof Newsfeed];
      const bValue = b[orderBy as keyof Newsfeed];

      if (orderBy === 'is_active') {
        return order === 'asc'
          ? aValue === bValue
            ? 0
            : aValue
              ? -1
              : 1
          : aValue === bValue
            ? 0
            : aValue
              ? 1
              : -1;
      }

      if (orderBy === 'start_date' || orderBy === 'end_date') {
        const aDate = new Date(aValue as string);
        const bDate = new Date(bValue as string);
        return order === 'asc'
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    return filtered;
  }, [newsfeeds, filters, order, orderBy]);

  // Paginate the results
  const paginatedNewsfeeds = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedNewsfeeds.slice(
      startIndex,
      startIndex + rowsPerPage
    );
  }, [filteredAndSortedNewsfeeds, page, rowsPerPage]);

  // Handle filter changes
  const handleFilterChange = (
    field: keyof NewsfeedFilters,
    value: string
  ): void => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filters change
  };

  // Handle row click
  const handleRowClick = (event: React.MouseEvent, id: string): void => {
    navigate(`/newsfeed/${id}`);
  };

  // Prepare table data without actions column
  const tableData = paginatedNewsfeeds;

  // Show loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading newsfeeds: {error.message}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardHeader
          title={
            <Typography variant="h4" component="h1" color="primary">
              Newsfeeds
            </Typography>
          }
          subheader={
            <Typography variant="body1" color="text.secondary">
              Manage and monitor all newsfeed announcements and updates
            </Typography>
          }
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/newsfeed/create')}
              size="large"
            >
              Create Newsfeed
            </Button>
          }
        />
      </Card>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Search Newsfeeds"
              placeholder="Search by title or description..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={filters.isActive}
                label="Status Filter"
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                startAdornment={
                  <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedNewsfeeds.length} of{' '}
          {filteredAndSortedNewsfeeds.length} newsfeeds
          {filters.search && ` matching "${filters.search}"`}
          {filters.isActive !== 'all' && ` (${filters.isActive} only)`}
        </Typography>
      </Box>

      {/* Newsfeeds Table */}
      <TablePeo
        data={tableData}
        isFetching={isLoading}
        headCells={headCells}
        handleRowClick={handleRowClick}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        pagination={true}
      />

      {/* No Results Message */}
      {filteredAndSortedNewsfeeds.length === 0 && (
        <Paper
          elevation={1}
          sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No newsfeeds found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filters.search || filters.isActive !== 'all'
              ? 'Try adjusting your search criteria or filters.'
              : 'Create your first newsfeed to get started.'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
